import { useMemo, useState } from 'react';
import styles from './Calculator.module.css';

interface EndocriaRow {
  gen: number;
  f: number;
  homo: number;
  hetero: number;
}

interface EndocriaResult {
  t: number;
  f0: number;
  ft: number;
  homozygous: number;
  heterozygous: number;
  gens99: number;
  rows: EndocriaRow[];
  interpretation: string;
}

const getInterpretation = (ft: number): string => {
  if (ft >= 0.98) {
    return 'Homocigosis practica alcanzada. Linea altamente uniforme y apta como parental de hibridos.';
  }
  if (ft >= 0.9) {
    return 'Nivel alto de homocigosis. Una o dos generaciones adicionales alcanzan homocigosis practica.';
  }
  if (ft >= 0.7) {
    return 'Nivel moderado de homocigosis. Aun existe segregacion relevante.';
  }
  if (ft >= 0.4) {
    return 'Nivel bajo-moderado de homocigosis. Predomina heterocigosis en buena parte del genoma.';
  }
  return 'Nivel bajo de homocigosis. La poblacion sigue mayormente heterocigota.';
};

const format4 = (value: number): string => value.toFixed(4);
const format2 = (value: number): string => value.toFixed(2);

export const EndocriaCalculator: React.FC = () => {
  const [generations, setGenerations] = useState<string>('6');
  const [f0, setF0] = useState<string>('0');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<EndocriaResult | null>(null);

  const handleCalculate = (): void => {
    const t = Number(generations);
    const f0Num = Number(f0);
    setError('');

    if (
      Number.isNaN(t) ||
      Number.isNaN(f0Num) ||
      t < 0 ||
      t > 20 ||
      !Number.isInteger(t) ||
      f0Num < 0 ||
      f0Num > 1
    ) {
      setResult(null);
      setError('Ingrese valores validos: t entero entre 0 y 20; F0 entre 0 y 1.');
      return;
    }

    const ft = 1 - (1 - f0Num) * Math.pow(0.5, t);
    const homozygous = ft;
    const heterozygous = 1 - ft;

    let gens99: number;
    if (f0Num === 0) {
      gens99 = Math.ceil(Math.log(1 - 0.99) / Math.log(0.5));
    } else {
      const target = (1 - 0.99) / (1 - f0Num);
      gens99 = target > 0 ? Math.ceil(Math.log(target) / Math.log(0.5)) : 0;
    }

    const rows: EndocriaRow[] = [];
    const limit = Math.min(t + 2, 15);
    for (let i = 0; i <= limit; i += 1) {
      const fi = 1 - (1 - f0Num) * Math.pow(0.5, i);
      rows.push({
        gen: i,
        f: fi,
        homo: fi * 100,
        hetero: (1 - fi) * 100,
      });
    }

    setResult({
      t,
      f0: f0Num,
      ft,
      homozygous,
      heterozygous,
      gens99,
      rows,
      interpretation: getInterpretation(ft),
    });
  };

  const chart = useMemo(() => {
    if (!result) {
      return null;
    }

    const width = 920;
    const height = 340;
    const padLeft = 56;
    const padRight = 30;
    const padTop = 24;
    const padBottom = 44;
    const innerWidth = width - padLeft - padRight;
    const innerHeight = height - padTop - padBottom;
    const maxGen = result.rows[result.rows.length - 1].gen || 1;

    const x = (generation: number): number => padLeft + (generation / maxGen) * innerWidth;
    const y = (value01: number): number => padTop + (1 - value01) * innerHeight;

    const homoPath = result.rows
      .map((row, idx) => `${idx === 0 ? 'M' : 'L'} ${x(row.gen)} ${y(row.f)}`)
      .join(' ');
    const heteroPath = result.rows
      .map((row, idx) => `${idx === 0 ? 'M' : 'L'} ${x(row.gen)} ${y(row.hetero / 100)}`)
      .join(' ');

    const yTicks = Array.from({ length: 6 }, (_, i) => {
      const ratio = i / 5;
      const value = 1 - ratio;
      const yPos = padTop + ratio * innerHeight;
      return { value, yPos };
    });

    const step = Math.max(1, Math.ceil(maxGen / 10));
    const xTicks: number[] = [];
    for (let i = 0; i <= maxGen; i += step) {
      xTicks.push(i);
    }
    if (xTicks[xTicks.length - 1] !== maxGen) {
      xTicks.push(maxGen);
    }

    return {
      width,
      height,
      padLeft,
      padRight,
      padBottom,
      x,
      y,
      homoPath,
      heteroPath,
      yTicks,
      xTicks,
    };
  }, [result]);

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.endocriaBox}>
        <div className={styles.calculatorHeader}>
          <h2>Calculadora de Coeficiente de Endocria (F)</h2>
          <p>Determine homocigosis luego de generaciones sucesivas de autofecundacion.</p>
        </div>

        <div className={styles.inputSection}>
          <h3>Parametros de Endocria</h3>
          <div className={styles.inputGroup}>
            <label htmlFor="generations">Numero de Generaciones de Autofecundacion (t)</label>
            <input
              id="generations"
              type="number"
              step="1"
              min="0"
              max="20"
              value={generations}
              onChange={(event) => setGenerations(event.target.value)}
              placeholder="Ej: 6"
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="f0">Coeficiente de Endocria Inicial (F0)</label>
            <input
              id="f0"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={f0}
              onChange={(event) => setF0(event.target.value)}
              placeholder="Ej: 0"
            />
            <span className={styles.formulaHint}>
              0 = poblacion panmictica inicial, 1 = completamente endocriada
            </span>
          </div>

          <div className={styles.endocriaFormulaCard}>
            <strong>Formula:</strong> F<sub>t</sub> = 1 - (1 - F<sub>0</sub>) x (1/2)<sup>t</sup>
          </div>
        </div>

        <button type="button" className={styles.calculateButton} onClick={handleCalculate}>
          Calcular Endocria
        </button>

        {error && <div className={styles.errorBox}>{error}</div>}

        {result && chart && (
          <div className={styles.resultsSection}>
            <div className={styles.endocriaResultCard}>
              <h3>Coeficiente de Endocria Final</h3>
              <div className={styles.endocriaBigValue}>{format4(result.ft)}</div>
              <p className={styles.resultFormula}>{result.interpretation}</p>

              <h4>Nivel de Homocigosis</h4>
              <div className={styles.endocriaProgress}>
                <div className={styles.endocriaProgressFill} style={{ width: `${result.ft * 100}%` }}>
                  {(result.ft * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className={styles.endocriaInfoGrid}>
              <div className={styles.endocriaInfoCard}>
                <h4>Proporcion Homocigota</h4>
                <div className={styles.endocriaInfoValue}>{format2(result.homozygous * 100)}%</div>
              </div>
              <div className={styles.endocriaInfoCard}>
                <h4>Proporcion Heterocigota</h4>
                <div className={styles.endocriaInfoValue}>{format2(result.heterozygous * 100)}%</div>
              </div>
              <div className={styles.endocriaInfoCard}>
                <h4>Generaciones para F=0.99</h4>
                <div className={styles.endocriaInfoValue}>{result.gens99}</div>
              </div>
            </div>

            <div className={styles.chartContainer}>
              <h3>Evolucion del Coeficiente de Endocria</h3>
              <div className={styles.svgChartWrapper}>
                <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className={styles.svgChart}>
                  {chart.yTicks.map((tick) => (
                    <g key={`y-${tick.yPos}`}>
                      <line
                        x1={chart.padLeft}
                        y1={tick.yPos}
                        x2={chart.width - chart.padRight}
                        y2={tick.yPos}
                        className={styles.gridLine}
                      />
                      <text x={chart.padLeft - 8} y={tick.yPos + 4} className={styles.axisLabel} textAnchor="end">
                        {tick.value.toFixed(1)}
                      </text>
                    </g>
                  ))}
                  {chart.xTicks.map((tick) => (
                    <g key={`x-${tick}`}>
                      <line
                        x1={chart.x(tick)}
                        y1={chart.height - chart.padBottom}
                        x2={chart.x(tick)}
                        y2={chart.height - chart.padBottom + 5}
                        className={styles.axisTick}
                      />
                      <text
                        x={chart.x(tick)}
                        y={chart.height - chart.padBottom + 20}
                        className={styles.axisLabel}
                        textAnchor="middle"
                      >
                        S{tick}
                      </text>
                    </g>
                  ))}

                  <line
                    x1={chart.padLeft}
                    y1={chart.height - chart.padBottom}
                    x2={chart.width - chart.padRight}
                    y2={chart.height - chart.padBottom}
                    className={styles.axisLine}
                  />
                  <line
                    x1={chart.padLeft}
                    y1={24}
                    x2={chart.padLeft}
                    y2={chart.height - chart.padBottom}
                    className={styles.axisLine}
                  />

                  <path d={chart.homoPath} className={styles.chartPath} />
                  <path d={chart.heteroPath} className={styles.chartPathOrange} />

                  {result.rows.map((row) => (
                    <circle
                      key={`f-${row.gen}`}
                      cx={chart.x(row.gen)}
                      cy={chart.y(row.f)}
                      r={4}
                      className={styles.chartPoint}
                    />
                  ))}
                </svg>
              </div>
              <div className={styles.endocriaLegend}>
                <span className={styles.legendGreen}>Homocigosis (F)</span>
                <span className={styles.legendOrange}>Heterocigosis (1-F)</span>
              </div>
            </div>

            <div className={styles.generationTable}>
              <h3>Tabla por Generacion</h3>
              <table>
                <thead>
                  <tr>
                    <th>Generacion</th>
                    <th>F</th>
                    <th>% Homocigosis</th>
                    <th>% Heterocigosis</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.gen} className={row.gen === result.t ? styles.selectedRow : ''}>
                      <td>
                        <strong>S{row.gen}</strong>
                      </td>
                      <td>{format4(row.f)}</td>
                      <td>{format2(row.homo)}%</td>
                      <td>{format2(row.hetero)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.implicationsBox}>
              <h4>Implicaciones de la Endocria</h4>
              <ul>
                <li>Depresion endogamica: puede reducir vigor, fertilidad y rendimiento.</li>
                <li>Fijacion de alelos: favorables y deletereos pasan a homocigosis.</li>
                <li>Uniformidad: las lineas endocriadas muestran alta uniformidad fenotipica.</li>
                <li>Base para hibridos: lineas S6-S8 son utiles como parentales.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
